// Domain contract re-exports — import from here or directly from the sub-module.
export * from './auth';
export * from './organization';

// Enums for better type safety
export enum AttributeType {
  STRING = "STRING",
  BOOLEAN = "BOOLEAN",
  NUMBER = "NUMBER",
  SELECTION = "SELECTION"
}

export enum ProductStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  ARCHIVED = "ARCHIVED"
}

// AI Attribute Definition (new unified system)
export interface AIAttributeDefinition {
  id: string;
  organization_id: string;
  attribute_key: string;
  attribute_name: string;
  attribute_type: 'enum' | 'multi_select' | 'text' | 'number';
  attribute_scope: 'categories' | 'custom_attributes' | 'attributes_rich' | 'product';
  possible_values?: string[];
  square_attribute_definition_id?: string;
  sync_to_square: boolean;
  display_order: number;
  is_required: boolean;
  is_active: boolean;
  prompt_instruction?: string;
  default_value?: string;
  validation_rules?: Record<string, any>;
  notes?: string;
  created_at: Date;
  updated_at: Date;
}

export interface AIAttributeDefinitionCreate {
  attribute_key: string;
  attribute_name: string;
  attribute_type: 'enum' | 'multi_select' | 'text' | 'number';
  attribute_scope: 'categories' | 'custom_attributes' | 'attributes_rich' | 'product';
  possible_values?: string[];
  sync_to_square?: boolean;
  is_required?: boolean;
  is_active?: boolean;
  prompt_instruction?: string;
  default_value?: string;
  display_order?: number;
}

export interface AIAttributeDefinitionUpdate {
  attribute_name?: string;
  attribute_type?: 'enum' | 'multi_select' | 'text' | 'number';
  possible_values?: string[];
  sync_to_square?: boolean;
  is_required?: boolean;
  is_active?: boolean;
  prompt_instruction?: string;
  default_value?: string;
  display_order?: number;
}

export interface AIAttributeTemplate {
  id: string;
  name: string;
  description: string;
  attribute_count: number;
}

// Legacy AttributeDefinition (for backward compatibility with Square API)
export interface AttributeDefinitionBase {
  name: string;
  type: AttributeType;
  description?: string;
  allowed_object_types: string[];
  selection_options?: string[];
}

export interface AttributeBase {
  name: string;
  string_value?: string;
  boolean_value?: boolean;
  number_value?: number;
  selection_uid_value?: string;
  custom_attribute_definition_id?: string;
}

export interface ProductBase {
  name: string;
  description?: string;
  category_id?: string; // deprecated - use categories instead
  categories?: string[]; // new multi-category support
  status: ProductStatus;
  abbreviation?: string;
  label_color?: string;
  available_online: boolean;
  available_for_pickup: boolean;
  skip_modifier_screen: boolean;
  price_amount: number; // in cents
  currency: string;
  inventory_quantity?: number; // current stock quantity
  tax_ids?: string[];
  tax_exempt: boolean;
}

export interface CategoryBase {
  name: string;
  abbreviation?: string;
  parent_category_id?: string;
  is_top_level: boolean;
  online_visibility: boolean;
}

export interface ImageBase {
  name?: string;
  caption?: string;
  url?: string;
}

// Full models with Square IDs
export interface AttributeDefinition extends AttributeDefinitionBase {
  id: string;
  version?: number;
  updated_at?: Date;
  created_at?: Date;
}

export interface Attribute extends AttributeBase {
  definition_id: string;
  object_id: string;
  version?: number;
  updated_at?: Date;
  created_at?: Date;
}

export interface Product extends ProductBase {
  id: string;
  version?: number;
  is_deleted: boolean;
  present_at_all_locations: boolean;
  present_at_location_ids?: string[];
  attributes?: Attribute[];
  image_ids?: string[];
  tags?: string[]; // Legacy field for compatibility
  sku?: string; // Legacy field for compatibility  
  images?: ProductImage[]; // Legacy field for compatibility
  updated_at?: Date;
  created_at?: Date;
}

export interface Category extends CategoryBase {
  id: string;
  version?: number;
  is_deleted: boolean;
  present_at_all_locations: boolean;
  present_at_location_ids?: string[];
  path_to_root?: Array<{ [key: string]: string }>;
  updated_at?: Date;
  created_at?: Date;
}

export interface Image extends ImageBase {
  id: string;
  version?: number;
  updated_at?: Date;
  created_at?: Date;
}

// Create models (for POST requests)
export type AttributeDefinitionCreate = AttributeDefinitionBase;

export interface AttributeCreate extends AttributeBase {
  definition_id: string;
  object_id: string;
}

export interface ProductCreate extends ProductBase {
  attributes?: AttributeCreate[];
  image_ids?: string[];
}

export type CategoryCreate = CategoryBase;

export type ImageCreate = ImageBase;

// Update models (for PUT/PATCH requests)
export interface AttributeDefinitionUpdate {
  name?: string;
  description?: string;
  selection_options?: string[];
}

export interface AttributeUpdate {
  name?: string;
  string_value?: string;
  boolean_value?: boolean;
  number_value?: number;
  selection_uid_value?: string;
}

export interface ProductUpdate {
  name?: string;
  description?: string;
  category_id?: string; // deprecated - use categories instead
  categories?: string[]; // new multi-category support
  status?: ProductStatus;
  abbreviation?: string;
  label_color?: string;
  available_online?: boolean;
  available_for_pickup?: boolean;
  skip_modifier_screen?: boolean;
  price?: number; // in dollars (will be converted to cents)
  price_amount?: number; // in cents
  currency?: string;
  inventory_quantity?: number; // current stock quantity
  sku?: string;
  image_ids?: string[];
}

export interface CategoryUpdate {
  name?: string;
  abbreviation?: string;
  parent_category_id?: string;
  online_visibility?: boolean;
}

export interface ImageUpdate {
  name?: string;
  caption?: string;
}

// Delete models (for DELETE requests)
export interface AttributeDefinitionDelete {
  id: string;
  version?: number;
}

export interface AttributeDelete {
  definition_id: string;
  object_id: string;
}

export interface ProductDelete {
  id: string;
  version?: number;
}

export interface CategoryDelete {
  id: string;
  version?: number;
}

export interface ImageDelete {
  id: string;
}

// API Response models
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface APIResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: string[];
  // SC-072: HTTP status code on a non-ok response, so callers can branch on
  // a specific status (e.g. 400 for a rejected pagination cursor) instead
  // of matching on the prose error message. Undefined on success.
  status?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface PaginatedResponse<T = any> {
  items: T[];
  total_count?: number;
  cursor?: string;
  has_more: boolean;
}

// Relationship models
export interface ProductAttributeRelation {
  product_id: string;
  attribute_definition_id: string;
  attribute_value: Attribute;
}

export interface ProductImageRelation {
  product_id: string;
  image_id: string;
  sort_order?: number;
}

export interface ProductCategoryRelation {
  product_id: string;
  category_id: string;
}

// AI-related interfaces (kept for existing AI functionality)
export interface ProductImage {
  id: string;
  url: string;
  type: 'front' | 'back' | 'detail' | 'primary' | 'additional' | 'image';  // 'image' for generic slots
  label: string;
  file?: File;
  order?: number; // Position in upload order (0 = primary image)
  needsUrlResolution?: boolean;
  // Temporary file fields (from Google Drive import)
  temp_path?: string;
  drive_file_id?: string;
  original_name?: string;
  filename?: string; // Generated filename on server
}

export interface CreateProductRequest {
  name: string;
  description: string;
  sku: string;
  price: number;
  currency: string;
  inventory_quantity: number;
  category_id?: string;        // Deprecated, keep for backward compat
  categories?: string[];        // Array of category IDs
  attributes: Array<{name: string; string_value: string}>; // AI attributes for database storage
  images: ProductImage[];
  image_ids?: string[]; // Square image IDs from uploaded images
  tags: string[];
  // AI-generated description fields
  product_title?: string;
  market_description?: string;
  detailed_inventory_description?: string;
}
export interface AIProductSuggestion {
  name: string;
  description: string;
  sku: string;
  suggestedAttributes: Record<string, string | string[]>;
  tags: string[];
}

export interface AIExecuteRequest {
  front_image?: {
    base64_encoded_image: string;
    media_type: string;
  };
  back_image?: {
    base64_encoded_image: string;
    media_type: string;
  };
  detail_image?: {
    base64_encoded_image: string;
    media_type: string;
  };
}

// New AI response structure interfaces
export interface SquareProjection {
  categories: string[];
  custom_attributes: {
    'k.audience': 'MEN' | 'WOMEN' | 'CHILD' | 'UNISEX';
    'k.size_category': 'ADULT' | 'CHILD';
    'k.occasion': 'FORMAL' | 'SEMI_FORMAL' | 'CEREMONY' | 'CASUAL';
    'k.season': 'SUMMER' | 'WINTER' | 'SPRING' | 'AUTUMN' | 'ALL_SEASON';
    'k.primary_color': 'Navy' | 'Black' | 'Red' | 'Blue' | 'White' | 'Pink' | 'Green' | 'Purple' | 'Brown' | 'Gray' | 'Cream' | 'Gold' | 'Silver' | 'Multi_Color';
    'k.era': 'MEIJI' | 'TAISHO' | 'SHOWA_EARLY' | 'SHOWA_MID' | 'SHOWA_LATE' | 'HEISEI' | 'MODERN';
    'k.material': 'SILK' | 'COTTON' | 'SYNTHETIC' | 'BLEND';
  };
  product_title: string;
  market_description: string;
}

export interface InternalRecord {
  category_raw: string;
  gender_raw: string;
  size_category_raw: string;
  condition: 'Museum_Quality' | 'Excellent' | 'Very_Good' | 'Good' | 'Fair' | 'Poor';
  confidence_level: 'High' | 'Medium' | 'Low';
  attributes_rich: Array<{
    key: string;
    value: string;
  }>;
  detailed_inventory_description: string;
}

// New JSON Template Response Structure (v2.0)
export interface SquareData {
  title: string;
  description: string;
  categories: string[];
  price_cents?: number;
  audience?: string;
  garment_category?: string;
  era?: string;
  material?: string;
  [key: string]: any; // Allow additional template-specific fields
}

export interface SquareNotes {
  [key: string]: string; // Notes for each square field
}

export interface InternalData {
  technical_description: string;
  confidence_level: 'High' | 'Medium' | 'Low';
  condition?: string;
  [key: string]: any; // Allow additional template-specific fields
}

export interface InternalNotes {
  [key: string]: string; // Notes for each internal field
}

export interface JSONTemplateResponse {
  square: SquareData;
  square_notes: SquareNotes;
  internal: InternalData;
  internal_notes: InternalNotes;
}

export interface AIExecuteResponse {
  // JSON Template structure (v2.0 - newest)
  square?: SquareData;
  square_notes?: SquareNotes;
  internal?: InternalData;
  internal_notes?: InternalNotes;

  // LinkML structure (v1.0 - legacy)
  sku_context_note?: string;
  square_projection?: SquareProjection;
  internal_record?: InternalRecord;

  // Legacy fields for backward compatibility
  id: string;
  name: string;
  description: string;
  sku: string;
  tags: string[];
  attributes: Record<string, string | string[]>;
  categoryId?: string;
  images: ProductImage[];
  confidence: number;
  processing_time: number;

  // CLASSIFICATION (legacy)
  category?: string;           // "Kimono/Haori/Obi"
  gender?: string;             // "Women's/Men's/Unisex"
  size_category?: string;      // "Adult/Child"
  occasion?: string;           // "Formal/Casual/Ceremony"
  season?: string;             // "Summer/Winter/All-season"
  primary_color?: string;      // "Blue/Red/Black"

  // PRODUCT DETAILS (legacy)
  era?: string;                // Historical context
  condition?: string;          // Quality indicator

  // MARKETING (legacy)
  product_title?: string;
  market_description?: string;
  detailed_inventory_description?: string;
}

// SC-072: cursor-based paginated response, replacing the page/pageSize/
// totalPages shape that the backend never honored (SC-069 made the
// backend forward-only via a signed cursor; page/per_page in its response
// are cosmetic). `total` is cumulative-delivered-so-far, not an upfront
// grand total: do not render it as "N of M" or an inventory count.
export interface CursorPaginatedResponse<T> {
  data: T[];
  nextCursor: string | null;
  total: number;
}

// SC-081: an organization's last-used label print size, GET/PUT at
// /organizations/{org_id}/label-print-settings. GET returns `null` (not this
// shape) when the org has never printed a label.
export interface LabelPrintSettings {
  width_in: number;
  height_in: number;
  preset: string | null;
}
